import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Package2, ChevronDown, Calculator } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { getStockItems, StockItem, createStockTransaction } from '../lib/stockItems';
import { ProductPhotoService } from '../services/productPhotoService';

interface WithdrawStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function WithdrawStockModal({
  isOpen,
  onClose,
  onSuccess
}: WithdrawStockModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<StockItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      loadItems();
    }
  }, [isOpen]);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const data = await getStockItems(selectedBusiness.id);
      setItems(data);
      
      // Load photos for all items
      await loadProductPhotos(data.map(item => item.id));
    } catch (err) {
      console.error('Error loading items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load items');
    }
  };

  const loadProductPhotos = async (stockItemIds: string[]) => {
    if (stockItemIds.length === 0) return;

    try {
      const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
      
      // Convert to the format expected by the component
      const photoUrls: Record<string, string> = {};
      Object.entries(photoMap).forEach(([itemId, photo]) => {
        photoUrls[itemId] = photo.photo_url;
      });
      
      setProductPhotos(photoUrls);
    } catch (err) {
      console.error('Error loading product photos:', err);
      // Don't set error state for photos, just continue without them
    }
  };

  // Component for item icon - shows photo if available, otherwise blue icon
  const ItemIcon = ({ item, size = 'medium' }: { item: StockItem; size?: 'small' | 'medium' }) => {
    const photoUrl = productPhotos[item.id];
    const sizeClasses = size === 'small' ? 'h-10 w-10' : 'h-12 w-12';
    const iconSizeClasses = size === 'small' ? 'h-5 w-5' : 'h-6 w-6';
    
    if (photoUrl) {
      return (
        <div className={`${sizeClasses} rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center`}>
          <img 
            src={photoUrl} 
            alt={item.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, hide it and show the blue icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = `
                  <div class="w-full h-full rounded-lg bg-blue-100 flex items-center justify-center">
                    <svg class="${iconSizeClasses} text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                    </svg>
                  </div>
                `;
              }
            }}
          />
        </div>
      );
    }
    
    // Default blue icon if no photo
    return (
      <div className={`${sizeClasses} rounded-lg bg-blue-100 flex items-center justify-center`}>
        <Package2 className={`${iconSizeClasses} text-blue-600`} />
      </div>
    );
  };

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    // First filter out deleted items
    const activeItems = items.filter(item => !item.is_deleted);
    
    const grouped = activeItems.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: StockItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Filter categories based on search query
  const filteredCategories = itemsByCategory.filter(category => {
    const searchLower = searchQuery.toLowerCase();
    return (
      category.name.toLowerCase().includes(searchLower) ||
      category.items.some(item => 
        item.name.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.sku?.toLowerCase().includes(searchLower)
      )
    );
  });

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setSelectedItem(null);
      setQuantity('');
      setNotes('');
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedItem) {
      setError('Please select an item');
      return;
    }

    const withdrawQuantity = parseInt(quantity);
    // Untracked (made-to-order) and oversellable items have no cap.
    if (selectedItem.track_inventory !== false && selectedItem.allow_oversell !== true && withdrawQuantity > selectedItem.quantity) {
      setError('Cannot withdraw more than current stock level');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await createStockTransaction({
        item_id: selectedItem.id,
        type: 'sale',
        quantity: withdrawQuantity,
        price: selectedItem.acquisition_cost,
        notes: notes || null
      });

      // Show success animation
      setShowSuccess(true);
      
      // Wait for animation, then reset form
      setTimeout(() => {
        setShowSuccess(false);
        setSelectedItem(null);
        setQuantity('');
        setNotes('');
        
        // Reload items to show updated quantities
        loadItems();
        
        // Notify parent to refresh (but don't close modal)
        onSuccess();
      }, 1000);
    } catch (err) {
      console.error('Error withdrawing stock:', err);
      setError(err instanceof Error ? err.message : 'Failed to withdraw stock');
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search query
  const filteredItems = React.useMemo(() => {
    if (!searchQuery) return [];
    const searchLower = searchQuery.toLowerCase();
    return items.filter(item => 
      !item.is_deleted &&
      (item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower) ||
      item.sku?.toLowerCase().includes(searchLower))
    );
  }, [items, searchQuery]);

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100010,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed right-0 w-full md:w-[700px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100050,
          ...(isMobile ? {} : {
            top: 'env(safe-area-inset-top, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
          })
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              Withdraw Stock
            </h2>
          </div>

          {/* Content Section */}
          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <span>{error}</span>
                  </div>
                </div>
              )}

              {/* Item Selection Section */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <label className="block text-sm font-semibold text-gray-900 mb-4">
                  Select Item to Withdraw
                </label>
                
                {/* Search Input */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search items..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                    style={{ fontSize: '16px' }}
                  />
                </div>

                {/* Show search results directly if searching */}
                {searchQuery ? (
                  <div className="space-y-2">
                    {filteredItems.length === 0 ? (
                      <div className="bg-white rounded-[12px] border border-gray-200/60 p-8 shadow-[0_1px_2px_rgba(0,0,0,0.05)] text-center">
                        <Package2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-sm font-semibold text-gray-900 mb-1">No items match your search</p>
                        <p className="text-sm text-gray-500">Try adjusting your search terms</p>
                      </div>
                    ) : (
                      filteredItems.map((item) => (
                        <div
                          key={item.id}
                          className={`bg-white rounded-[12px] border p-4 cursor-pointer active:scale-[0.98] transition-all duration-150 touch-manipulation ${
                            selectedItem?.id === item.id ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-200' : 'border-gray-200/60'
                          } shadow-[0_1px_2px_rgba(0,0,0,0.05)]`}
                          onClick={() => setSelectedItem(item)}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex items-center">
                            <div className="mr-4">
                              <ItemIcon item={item} size="medium" />
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-semibold text-gray-900 flex items-center space-x-2">
                                <span>{item.name}</span>
                                <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded-lg">
                                  {item.category?.name || 'Uncategorized'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-600 mt-1">
                                {item.track_inventory === false ? (
                                  <span className="font-medium text-gray-500">Made to order — no stock levels</span>
                                ) : (
                                  <>Current Stock: <span className="font-medium text-blue-700">{item.quantity} {item.unit}</span></>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {selectedItem?.id === item.id && (
                            <div className="mt-4 p-4 bg-blue-50 rounded-xl space-y-3">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  max={selectedItem.track_inventory === false || selectedItem.allow_oversell === true ? undefined : selectedItem.quantity}
                                  value={quantity}
                                  onChange={(e) => setQuantity(e.target.value)}
                                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                  style={{ fontSize: '16px' }}
                                  placeholder={`Quantity in ${selectedItem.unit}`}
                                />
                                <span className="text-sm text-gray-600 font-semibold">{selectedItem.unit}(s)</span>
                                <button
                                  type="submit"
                                  disabled={loading || showSuccess || !quantity || (selectedItem.track_inventory !== false && selectedItem.allow_oversell !== true && parseInt(quantity) > selectedItem.quantity)}
                                  className={`px-4 py-3 text-sm font-semibold text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                                    showSuccess ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                                  }`}
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {showSuccess ? (
                                    <span className="flex items-center gap-2">
                                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Success!
                                    </span>
                                  ) : loading ? 'Withdrawing...' : 'Withdraw'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredCategories.map((category) => (
                      <div key={category.id} className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleCategory(category.id)}
                          className="w-full px-4 py-3 flex items-center justify-between active:bg-gray-100 transition-colors bg-gray-50"
                          style={{ touchAction: 'manipulation' }}
                        >
                          <div className="flex items-center">
                            <ChevronDown 
                              className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                                expandedCategories.has(category.id) ? 'rotate-180' : ''
                              }`}
                            />
                            <span className="ml-3 text-sm font-semibold text-gray-900">
                              {category.name}
                            </span>
                          </div>
                        </button>

                        {expandedCategories.has(category.id) && (
                          <div className="bg-white divide-y divide-gray-200">
                            {category.items.map((item) => (
                              <div
                                key={item.id}
                                className={`p-4 cursor-pointer active:bg-gray-100 transition-colors ${
                                  selectedItem?.id === item.id ? 'bg-blue-50' : ''
                                }`}
                                onClick={() => setSelectedItem(item)}
                                style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="flex items-center">
                                  <div className="mr-3">
                                    <ItemIcon item={item} size="small" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-semibold text-gray-900">{item.name}</div>
                                    <div className="text-xs text-gray-600 mt-0.5">
                                      {item.track_inventory === false ? (
                                        <span className="font-medium text-gray-500">Made to order — no stock levels</span>
                                      ) : (
                                        <>Current Stock: <span className="font-medium text-blue-700">{item.quantity} {item.unit}</span></>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {selectedItem?.id === item.id && (
                                  <div className="mt-3 p-4 bg-blue-50 rounded-xl space-y-3">
                                    <div className="flex items-center space-x-3">
                                      <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        max={selectedItem.track_inventory === false || selectedItem.allow_oversell === true ? undefined : selectedItem.quantity}
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                                        style={{ fontSize: '16px' }}
                                        placeholder={`Quantity in ${selectedItem.unit}`}
                                      />
                                      <span className="text-sm text-gray-600 font-semibold">{selectedItem.unit}(s)</span>
                                      <button
                                        type="submit"
                                        disabled={loading || showSuccess || !quantity || (selectedItem.track_inventory !== false && selectedItem.allow_oversell !== true && parseInt(quantity) > selectedItem.quantity)}
                                        className={`px-4 py-3 text-sm font-semibold text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                                          showSuccess ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                                        }`}
                                        style={{ WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        {showSuccess ? (
                                          <span className="flex items-center gap-2">
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Success!
                                          </span>
                                        ) : loading ? 'Withdrawing...' : 'Withdraw'}
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Notes Section */}
              {selectedItem && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150 resize-none"
                    style={{ fontSize: '16px' }}
                    placeholder="Add any notes about this stock withdrawal..."
                  />
                </div>
              )}

              {/* Summary Section */}
              {selectedItem && (
                <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
                    <Calculator className="h-4 w-4 mr-2 text-blue-600" />
                    Withdrawal Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Item:</span>
                        <span className="text-sm font-semibold text-gray-900">{selectedItem.name}</span>
                      </div>
                    </div>
                    {selectedItem.track_inventory !== false && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Current Stock:</span>
                        <span className="text-sm font-semibold text-blue-600">{selectedItem.quantity} {selectedItem.unit}</span>
                      </div>
                    </div>
                    )}
                    <div className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Withdrawing:</span>
                        <span className="text-sm font-semibold text-red-600">{quantity || 0} {selectedItem.unit}</span>
                      </div>
                    </div>
                    {selectedItem.track_inventory !== false && (
                    <div className="bg-white rounded-[12px] border border-green-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" style={{ backgroundColor: 'rgba(240, 253, 244, 0.5)' }}>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-gray-700">Remaining Stock:</span>
                        <span className="text-lg font-semibold text-green-700">
                          {Math.max(0, selectedItem.quantity - (parseInt(quantity) || 0))} {selectedItem.unit}
                        </span>
                      </div>
                    </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures modal appears above navigation
  return createPortal(modalContent, document.body);
}